import { useMutation } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AuthLayout, AuthNotice } from '../components/AuthLayout.js'
import { Button, Field, PasswordInput } from '../components/ui.js'
import { errorMessage, fieldErrors, useTRPC } from '../lib/trpc.js'

export function ResetPassword() {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [mismatch, setMismatch] = useState(false)

  const reset = useMutation(
    trpc.auth.resetPassword.mutationOptions({
      onSuccess: () => navigate('/login?reset=1', { replace: true }),
    }),
  )

  if (!token) {
    return (
      <AuthLayout title="Link not valid">
        <div className="flex flex-col gap-4 text-sm text-ink-600">
          <p>That reset link is missing its token. Request a new one and try again.</p>
          <Link to="/forgot-password">
            <Button variant="primary" size="lg" block>
              Request a new link
            </Button>
          </Link>
        </div>
      </AuthLayout>
    )
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()

    // Checked here rather than only on the server: a typo in a password you
    // cannot see is worth catching before a round trip.
    if (password !== confirm) {
      setMismatch(true)
      return
    }

    setMismatch(false)
    reset.mutate({ token, password })
  }

  const errors = fieldErrors(reset.error)

  return (
    <AuthLayout title="Choose a new password">
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field
          label="New password"
          htmlFor="password"
          hint="At least 12 characters. Length beats punctuation."
          error={errors['password']}
          required
        >
          <PasswordInput
            id="password"
            autoComplete="new-password"
            required
            minLength={12}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <Field
          label="Confirm password"
          htmlFor="confirm"
          error={mismatch ? 'Those two do not match.' : undefined}
          required
        >
          <PasswordInput
            id="confirm"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </Field>

        {reset.error ? (
          <AuthNotice tone="error" icon="alert">
            {errorMessage(reset.error)}
          </AuthNotice>
        ) : null}

        <p className="text-sm text-ink-500">
          Setting a new password signs you out everywhere else.
        </p>

        <Button type="submit" variant="primary" size="lg" block loading={reset.isPending}>
          Set password
        </Button>
      </form>
    </AuthLayout>
  )
}
